import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputBase, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { CourseDetailEntity, CourseEntity, CoursePayload } from 'src/entities/Course.entity';
import { useDeleteCourseMutation, useUpdateCourseMutation } from 'src/hooks/useCourseMutations';
import { useCourseQuery } from 'src/hooks/useCourses.query';
import { parseCourseMasterError } from 'src/utils/courseMasterError';

import CourseBasicFields from './CourseBasicFields';
import { courseLabel, fmtStamp, tripLabel } from './courseMasterUtils';
import RouteEditor from './RouteEditor';
import TripGroupAlignDialog from './TripGroupAlignDialog';
import UnlinkTripDialog from './UnlinkTripDialog';
import { useCourseOptions } from './useCourseOptions';
import { useCourseSaveFlow } from './useCourseSaveFlow';
import { useIsCourseSaving } from './useIsCourseSaving';

const muted = 'text.secondary';
type Props = {
  courseId: number; courses: CourseEntity[]; selectedTimetableId: number | null; onSelectTimetable: (id: number | null) => void;
  /** 所属ダイヤの名前を押したとき。ダイヤ詳細を開く */
  onOpenTimetable: (id: number) => void; onDeleted: () => void };

export default function CourseDetail({ courseId, courses, selectedTimetableId, onSelectTimetable, onOpenTimetable, onDeleted }: Props) {
  const detail = useCourseQuery(courseId);
  if (detail.isPending) return <Box sx={{ p: 4, color: muted }}>読み込み中…</Box>;
  if (!detail.data) return <Box sx={{ p: 4, color: muted }}>コースを読み込めませんでした</Box>;
  return <CourseEditor key={courseId} course={detail.data} courses={courses} selectedTimetableId={selectedTimetableId} onSelectTimetable={onSelectTimetable} onOpenTimetable={onOpenTimetable} onDeleted={onDeleted} />;
}

/** 項目ごとに即保存（判断事項 3）。エラー code の分岐は useCourseSaveFlow。保存中（基本情報・ルート・適用終了のどれか）は詳細全体を操作不可にする */
function CourseEditor({ course, courses, selectedTimetableId, onSelectTimetable, onOpenTimetable, onDeleted }: { course: CourseDetailEntity } & Omit<Props, 'courseId'>) {
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync } = useUpdateCourseMutation();
  const destroy = useDeleteCourseMutation();
  const { trucks } = useCourseOptions();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const send = useCallback((data: CoursePayload) => mutateAsync({ id: course.id, data }), [mutateAsync, course.id]);
  const saved = useCallback(() => enqueueSnackbar('保存しました', { variant: 'success' }), [enqueueSnackbar]);
  const flow = useCourseSaveFlow(send, saved, course.id);
  const saving = useIsCourseSaving(course.id) || flow.pending;
  const patch = (data: CoursePayload) => { void flow.run(data); };
  // Why not 失敗を放っておく: defaultValue の欄はサーバの値が変わらないと作り直されず、送った値のまま残って保存済みに見える
  const saveText = (data: CoursePayload) => flow.run(data);
  const saveInput = (input: HTMLInputElement | HTMLTextAreaElement, server: string, data: CoursePayload) => {
    void saveText(data).then((ok) => { if (!ok) input.value = server; });
  };
  const tabId = course.timetables.some((t) => t.id === selectedTimetableId) ? selectedTimetableId : (course.timetables[0]?.id ?? null);
  const timetable = course.timetables.find((t) => t.id === tabId) ?? null;
  const route = course.routes.find((r) => (tabId == null ? r.timetable == null : r.timetable?.id === tabId)) ?? null;
  // サンプル（route-planner d9aa3bb）の見出しの欄。編集できる欄だと分かるよう常に実線の枠を出す
  const frame = { display: 'flex', alignItems: 'center', height: 42, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, '&:hover': { borderColor: 'text.secondary' } };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        <Tooltip title="クリックして編集できます">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 14, color: muted, whiteSpace: 'nowrap' }}>コース番号：</Typography>
            <Box sx={frame}>
              <InputBase
                key={`code-${course.code}`}
                defaultValue={course.code}
                inputProps={{ 'aria-label': 'コース番号' }}
                disabled={saving}
                sx={{ '&&': { fontSize: 24, fontWeight: 700 }, width: 110 }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (!v || v === course.code) {
                    e.target.value = course.code;
                    return;
                  }
                  saveInput(e.target, course.code, { code: v });
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 16, color: muted, ml: 1 }}>{tripLabel(course.tripNo)}</Typography>
          </Box>
        </Tooltip>
        <Tooltip title="クリックして編集できます">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 280 }}>
            <Typography sx={{ fontSize: 14, color: muted, whiteSpace: 'nowrap' }}>コース名：</Typography>
            <Box sx={{ ...frame, flex: 1 }}>
              <InputBase
                key={`name-${course.name ?? ''}`}
                defaultValue={course.name ?? ''}
                placeholder="未設定"
                inputProps={{ 'aria-label': 'コース名' }}
                disabled={saving}
                sx={{ '&&': { fontSize: 16, fontWeight: 600 }, width: '100%' }}
                onBlur={(e) => {
                  const v = e.target.value.trim() || null;
                  if (v !== course.name) saveInput(e.target, course.name ?? '', { name: v });
                }}
              />
            </Box>
          </Box>
        </Tooltip>
      </Box>
      {flow.alert && <Alert severity="error" sx={{ mb: 2 }} onClose={flow.clearAlert}>{flow.alert}</Alert>}
      <CourseBasicFields course={course} courses={courses} disabled={saving} onChange={patch} onSaveText={saveText} onUnlink={() => setUnlinkOpen(true)} />
      <Box sx={{ display: 'flex', gap: 4, mb: 2, fontSize: 13, color: muted }}>
        <span>作成日時：{fmtStamp(course.createdAt)}</span><span>更新日時：{fmtStamp(course.updatedAt)}</span>
      </Box>
      {course.linkedTrips.length > 0 && (
        <Typography sx={{ mb: 2, color: muted, fontSize: 14 }}>
          紐付く便：{course.linkedTrips.map(courseLabel).join(' / ')}
        </Typography>
      )}
      <Box sx={{ mb: 2.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 13, color: muted }}>所属ダイヤ：</Typography>
        {course.timetables.length
          ? course.timetables.map((t, index) => (
            <Box key={t.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
              {index > 0 && <Typography sx={{ fontSize: 14, color: muted }}>／</Typography>}
              <Button
                size="small"
                aria-label={`${t.name}ダイヤの詳細を開く`}
                onClick={() => onOpenTimetable(t.id)}
                sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 14, textDecoration: 'underline', textUnderlineOffset: '3px' }}
              >
                {t.name}
              </Button>
            </Box>
          ))
          : <Typography sx={{ fontSize: 14 }}>未所属（ダイヤ詳細の「コースを追加」で入れる）</Typography>}
      </Box>
      {/* key でダイヤごとに作り直す: エラー中の draft を別ダイヤのルートへ送らない（判断事項 12） */}
      <RouteEditor key={tabId ?? 'unassigned'} course={course} route={route} timetable={timetable} timetables={course.timetables} onSelectTimetable={onSelectTimetable} disabled={saving} />
      {/* 2 便が紐付いている 1 便は消せない（C3 ルール 5 (a)）。ボタンは非活性で残し、理由を横に出す */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} disabled={saving || course.linkedTrips.length > 0} onClick={() => setDeleteOpen(true)}>コースを削除</Button>
        {course.linkedTrips.length > 0 && (
          <Typography sx={{ fontSize: 13, color: muted }}>先に 2 便の紐付けを外してください（{course.linkedTrips.map(courseLabel).join('、')} がこのコースに紐付いています）</Typography>
        )}
      </Box>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>コースを削除しますか？</DialogTitle>
        <DialogContent sx={{ fontSize: 14 }}>{courseLabel(course)}{course.name ? `：${course.name}` : ''} を削除します。所属ダイヤからも除外されます。案件から参照されていれば一覧から消え、同じコードで作り直すと古い方は「{course.code}-旧」になります。</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={destroy.isPending}
            onClick={() => destroy.mutate({ id: course.id }, {
              onSuccess: () => { setDeleteOpen(false); onDeleted(); enqueueSnackbar('コースを削除しました', { variant: 'success' }); },
              onError: (e) => enqueueSnackbar(parseCourseMasterError(e, '削除できませんでした').message, { variant: 'error' }),
            })}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
      <UnlinkTripDialog
        open={unlinkOpen}
        course={course}
        trucks={trucks}
        onClose={() => setUnlinkOpen(false)}
        onConfirm={(truckId) => { setUnlinkOpen(false); patch({ trip_no: 1, default_truck_id: truckId }); }}
      />
      <TripGroupAlignDialog mismatch={flow.mismatch?.error ?? null} selfLabel={courseLabel(course)} onChoose={flow.resolveMismatch} onClose={flow.dismissMismatch} />
    </Box>
  );
}
